// frontend/src/components/features/GamePreviewModal.jsx
import React, { useState } from "react";
import PlayListButton from "../common/PlayListButton";
import HouseRulesSection from "./HouseRulesSection";
import { FIND_SIMILAR_ICON, OPEN_IN_EXPLORE_ICON } from "../../constants/icons";
import './ModalComponents.css';

function GamePreviewModal({
  game,
  onClose,
  onSearchSimilar,
  onOpenInExplore,
  user,
  onTogglePlayList,
  inPlayList,
  hideActions,
  letsPlayConfig,
}) {
  const [activeTab, setActiveTab] = useState("details");

  if (!game) {
    return null;
  }

  const handleSearchSimilar = () => {
    if (onSearchSimilar) {
      onSearchSimilar(game);
    }
    onClose();
  };

  const handleOpenInExplore = () => {
    if (onOpenInExplore) {
      onOpenInExplore(game);
    }
    onClose();
  };

  const handleLogSession = () => {
    if (letsPlayConfig?.onLogSession) {
      letsPlayConfig.onLogSession(game);
      onClose();
    }
  };

  const handleOpenScorePad = () => {
    if (letsPlayConfig?.onOpenScorePad) {
      letsPlayConfig.onOpenScorePad(game);
      onClose();
    }
  };

  const isAnonymous = !user || !user.id;
  const gameId = game.id ?? game.game_id;

  const tabs = [
    { id: "details", label: "Details" },
    { id: "saved", label: "House Rules" },
    { id: "score", label: "Score & Log" },
  ];

  const renderDetails = () => (
    <>
      {game.thumbnail && (
        <img
          src={game.thumbnail}
          alt={game.name}
          style={{
            maxWidth: "200px",
            maxHeight: "200px",
            width: "auto",
            height: "auto",
            objectFit: "contain",
            borderRadius: "4px",
            marginBottom: "1rem",
            display: "block",
            marginLeft: "auto",
            marginRight: "auto"
          }}
        />
      )}

      {((game.designers && game.designers.length > 0) || (game.shared_designers && game.shared_designers.length > 0)) && (
        <div style={{ marginBottom: "1rem" }}>
          <h3>Designers</h3>
          <div className="designers-list">
            {(game.designers || game.shared_designers || []).map((d, idx) => (
              <span key={idx} className="designer-tag">
                {d}
              </span>
            ))}
          </div>
        </div>
      )}

      {((game.artists && game.artists.length > 0) || (game.shared_artists && game.shared_artists.length > 0)) && (
        <div style={{ marginBottom: "1rem" }}>
          <h3>Artists</h3>
          <div className="artists-list">
            {(game.artists || game.shared_artists || []).map((a, idx) => (
              <span key={idx} className="artist-tag">
                {a}
              </span>
            ))}
          </div>
        </div>
      )}

      {((game.mechanics && game.mechanics.length > 0) || (game.shared_mechanics && game.shared_mechanics.length > 0)) && (
        <div style={{ marginBottom: "1rem" }}>
          <h3>Mechanics</h3>
          <div className="mechanics-list">
            {(game.mechanics || game.shared_mechanics || []).map((m, idx) => (
              <span key={idx} className="mechanic-tag">
                {m}
              </span>
            ))}
          </div>
        </div>
      )}

      {((game.categories && game.categories.length > 0) || (game.shared_categories && game.shared_categories.length > 0)) && (
        <div style={{ marginBottom: "1rem" }}>
          <h3>Categories</h3>
          <div className="categories-list">
            {(game.categories || game.shared_categories || []).map((c, idx) => (
              <span key={idx} className="category-tag">
                {c}
              </span>
            ))}
          </div>
        </div>
      )}
    </>
  );

  const renderSavedInfo = () => (
    <HouseRulesSection gameId={gameId} user={user} isAnonymous={isAnonymous} />
  );

  const renderScoreLog = () => (
    <div className="score-log-tab">
      <p className="score-log-tab-question">What would you like to do?</p>
      {(game.thumbnail || game.image_url) && (
        <img
          src={game.thumbnail || game.image_url}
          alt={game.name || `Game ${game.game_id}`}
          style={{
            maxWidth: "200px",
            maxHeight: "200px",
            width: "auto",
            height: "auto",
            objectFit: "contain",
            borderRadius: "4px",
            marginBottom: "1rem",
            display: "block",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        />
      )}
      <div className="score-log-tab-actions">
        <button
          onClick={handleLogSession}
          className="game-preview-action-btn primary"
          disabled={isAnonymous || !letsPlayConfig?.onLogSession}
          title={isAnonymous ? "Login to log a session" : "Add this game to your timeline"}
          data-testid="score-log-add-timeline"
        >
          Add to Timeline
        </button>
        <button
          onClick={handleOpenScorePad}
          className="game-preview-action-btn primary"
          disabled={isAnonymous || !letsPlayConfig?.onOpenScorePad}
          title={isAnonymous ? "Login to use the score pad" : "Open the score pad"}
          data-testid="score-log-open-scorepad"
        >
          Open Score Pad
        </button>
      </div>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content game-preview-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="game-preview-header">
          <div className="game-preview-title">
            <div className="game-preview-title-row">
              <h2>{game.name || `Game ${game.game_id}`}</h2>
              <div className="game-preview-title-actions">
                {!hideActions && (
                  <>
                    {onSearchSimilar && (
                      <button
                        onClick={handleSearchSimilar}
                        className="game-preview-icon-button"
                        title="Find similar games"
                        aria-label="Find similar games"
                        data-testid="game-preview-find-similar"
                      >
                        {FIND_SIMILAR_ICON}
                      </button>
                    )}
                    {onOpenInExplore && (
                      <button
                        onClick={handleOpenInExplore}
                        className="game-preview-icon-button"
                        title="Open this game in Explore"
                        aria-label="Open this game in Explore"
                        data-testid="game-preview-open-explore"
                      >
                        {OPEN_IN_EXPLORE_ICON}
                      </button>
                    )}
                    {onTogglePlayList && (
                      <PlayListButton
                        isActive={inPlayList}
                        isAnonymous={isAnonymous}
                        onClick={() => onTogglePlayList(game)}
                        showLabel={false}
                        showIcon={true}
                        className="game-preview-icon-button"
                        dataTestId="game-preview-playlist-button"
                      />
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="game-preview-meta">
              {game.year_published && (
                <span className="game-preview-meta-item">
                  {game.year_published}
                </span>
              )}
              {game.average_rating && (
                <span className="game-preview-meta-item">
                  ⭐ {game.average_rating.toFixed(1)}
                </span>
              )}
              {(game.min_players || game.max_players) && (
                <span className="game-preview-meta-item">
                  {game.min_players || "?"} - {game.max_players || "?"} players
                </span>
              )}
              {(game.playing_time || game.min_playtime || game.max_playtime) && (
                <span className="game-preview-meta-item">
                  {
                    game.playing_time ? `${game.playing_time} min` :
                    game.min_playtime && game.max_playtime ? `${game.min_playtime}-${game.max_playtime} min` :
                    game.min_playtime ? `${game.min_playtime}+ min` :
                    game.max_playtime ? `Up to ${game.max_playtime} min` :
                    "?"
                  }
                </span>
              )}
              {game.avg_weight && (
                <span className="game-preview-weight">
                  {game.avg_weight.toFixed(1)}/5 weight
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="modal-close"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <div className="game-preview-tabs" role="tablist" aria-label="Game preview tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`game-preview-tabpanel-${tab.id}`}
              className={`game-preview-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`game-preview-tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div
          className="game-preview-content"
          role="tabpanel"
          id={`game-preview-tabpanel-${activeTab}`}
        >
          {activeTab === 'details' && renderDetails()}
          {activeTab === 'saved' && renderSavedInfo()}
          {activeTab === 'score' && renderScoreLog()}
        </div>
      </div>
    </div>
  );
}

export default GamePreviewModal;
